import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FaLock as LockIcon,
  FaLockOpen as UnlockIcon,
  FaStop as StopIcon,
} from 'react-icons/fa'
import {
  createScrubPerformer,
  type ScrubPerformer as Performer,
  type Zone as PerformerZone,
} from '@/lib/scrubPerformer'
import type { SampleSlice } from '@/hooks/useSampleUpload'
import { useUIStore } from '@/stores/uiStore'
import type { BankId } from '@busswerks/shared'

const BANK_PICKER_OPTIONS: ReadonlyArray<BankId> = ['A', 'B', 'C', 'D']

/**
 * Tempera-style performance grid. 16 vertical columns × 1 row,
 * grouped into 4 zones of 4 columns each. X axis maps to the
 * sample timeline (column N covers `N/16 … (N+1)/16` of the
 * duration, so the scrub pad's horizontal axis lines up with the
 * waveform above it). Y axis maps to pitch offset (±12 st) with a
 * small zero-detent around centre.
 *
 * Each zone is a different performance effect:
 *
 *   Zone 1 (cols 0–3):   GATE   — AM-chopped gating at 1/32, 1/16,
 *                                 1/8, 1/4 rates. DJ-style cuts.
 *   Zone 2 (cols 4–7):   ROLL   — grain retrigger at 16, 8, 4, 2
 *                                 per beat. Drum-fill rolls.
 *   Zone 3 (cols 8–11):  DELAY  — feedback delay variants (short/long
 *                                 × low-fb/high-fb).
 *   Zone 4 (cols 12–15): BOUNCE — per-trigger position randomisation
 *                                 across slice boundaries with
 *                                 increasing jitter.
 *
 * Effect DSP lives in `@/lib/scrubPerformer.ts` — this component
 * is the input surface + visual state, the performer owns the
 * GrainPlayer + effect chain (square-LFO-modulated gain for Gate,
 * short grainSize for Roll, Tone.FeedbackDelay for Delay, wall-
 * clock timer with slice-indexed jumps for Bounce).
 *
 * Latch toggle pins the last touched cell so the user can leave a
 * cell engaged while both hands play the pads above — matches the
 * "Latch mode" from the Red Means Recording jungle walkthrough.
 */

type Zone = 'gate' | 'roll' | 'delay' | 'bounce'

const COLS = 16
const COLS_PER_ZONE = 4

interface ZoneDef {
  zone: Zone
  label: string
  /** Tailwind class applied to the zone's divider + highlight bar
   *  so each zone gets a unique colour hint. Keep these low-sat so
   *  they read as UI annotation, not cell state. */
  accent: string
  bg: string
}

const ZONES: ZoneDef[] = [
  {
    zone: 'gate',
    label: 'GATE',
    accent: 'bg-rose-500/60',
    bg: 'bg-rose-500/[0.04]',
  },
  {
    zone: 'roll',
    label: 'ROLL',
    accent: 'bg-amber-500/60',
    bg: 'bg-amber-500/[0.04]',
  },
  {
    zone: 'delay',
    label: 'DELAY',
    accent: 'bg-sky-500/60',
    bg: 'bg-sky-500/[0.04]',
  },
  {
    zone: 'bounce',
    label: 'BOUNCE',
    accent: 'bg-violet-500/60',
    bg: 'bg-violet-500/[0.04]',
  },
]

/** Human-readable variant labels per zone. Index matches column
 *  modulo 4 — col 0 of each zone gets labels[0], etc. */
const VARIANT_LABELS: Record<Zone, [string, string, string, string]> = {
  gate: ['1/32', '1/16', '1/8', '1/4'],
  roll: ['×16', '×8', '×4', '×2'],
  delay: ['16·l', '16·h', '8·l', '8·h'],
  bounce: ['±1', '±2', '±4', 'rnd'],
}

function zoneDefForCol(col: number): ZoneDef {
  return ZONES[Math.min(ZONES.length - 1, Math.floor(col / COLS_PER_ZONE))]
}

function variantIndexForCol(col: number): number {
  return col % COLS_PER_ZONE
}

interface ScrubPadProps {
  buffer: AudioBuffer | null
  /** Target tempo the grain clock syncs to. */
  bpm: number | undefined
  /** Current slices — threaded through for the Bounce zone to
   *  pick jump targets from real slice boundaries. When absent
   *  or shorter than 2, Bounce falls back to raw grain emission. */
  slices?: SampleSlice[]
}

/** Selectable loop-region lengths. Value = beats. 1/4 note is
 *  the default — it matches "four grain periods of a 16th-grain",
 *  which is what Stage 2a shipped with. Raising it audibly
 *  extends how much of the sample you hear from each cell
 *  (useful for pads / freeze moves); shrinking it tightens into
 *  single-grain retriggers. */
const LOOP_LENGTHS: { label: string; beats: number }[] = [
  { label: '1/16', beats: 0.25 },
  { label: '1/8', beats: 0.5 },
  { label: '1/4', beats: 1 },
  { label: '1/2', beats: 2 },
  { label: '1 bar', beats: 4 },
  { label: '2 bar', beats: 8 },
  { label: '4 bar', beats: 16 },
]

/** Selectable grain-size overrides. `null` = zone default. */
const GRAIN_OVERRIDES: { label: string; beats: number | null }[] = [
  { label: 'Auto', beats: null },
  { label: '1/32', beats: 0.125 },
  { label: '1/16', beats: 0.25 },
  { label: '1/8', beats: 0.5 },
  { label: '1/4', beats: 1 },
]

export function ScrubPad({ buffer, bpm, slices }: ScrubPadProps) {
  const [playing, setPlaying] = useState(false)
  const [latched, setLatched] = useState(false)

  // Bank-override glue (PR 5 of the scrub-performance rollout): the
  // ScrubPad reports its engaged state + target bank into uiStore so
  // the trigger path in `useSequencerTriggers` can mute the target
  // bank's pad audio whenever scrub is active. Session-only state.
  const scrubTargetBankId = useUIStore(s => s.scrubTargetBankId)
  const setScrubTargetBankId = useUIStore(s => s.setScrubTargetBankId)
  const setScrubEngaged = useUIStore(s => s.setScrubEngaged)
  useEffect(() => {
    // "Engaged" = a zone is actively playing OR latched is pinning
    // one. When the user releases without latch, `playing` flips
    // false and the engaged signal drops — the pattern resumes.
    setScrubEngaged(playing || latched)
    return () => {
      // Clear on unmount (ScrubPad closes / project switches) so the
      // trigger path doesn't stay suppressed.
      setScrubEngaged(false)
    }
  }, [playing, latched, setScrubEngaged])
  // Active cell: `{ col, y }` — col is the 0..15 integer column
  // the user last touched; y is the normalised vertical position
  // inside that cell (0 = top, 1 = bottom), used for pitch.
  const [activeCol, setActiveCol] = useState<number | null>(null)
  const [activeY, setActiveY] = useState(0.5)
  // Live-performance params: applied to the currently-playing
  // grains without a retrigger so sliders are click-free.
  // Default one bar (4 beats) — matches how users audition drum
  // loops more often than "one 1/4 note worth of this position".
  // Pills let them shrink to tighter values when they want
  // retriggers / stutters.
  const [loopLengthBeats, setLoopLengthBeats] = useState(4)
  const [grainOverrideBeats, setGrainOverrideBeats] = useState<number | null>(
    null,
  )
  const [transpose, setTranspose] = useState(0) // semitones, -24..+24
  const [reverse, setReverse] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const performerRef = useRef<Performer | null>(null)
  const dragPointerRef = useRef<number | null>(null)

  const effectiveBpm = bpm && bpm > 0 ? bpm : 120
  const durationSec = buffer?.duration ?? 0

  // Slice boundaries as a flat number[] — what the performer's
  // Bounce zone expects. Derived once from the slice list so it
  // stays stable across renders when the user is just dragging.
  const slicePositions = useMemo(
    () => (slices ? slices.map(s => s.startSec) : []),
    [slices],
  )

  // Tear down + recreate the ScrubPerformer whenever the source
  // buffer changes.
  useEffect(() => {
    if (!buffer) {
      performerRef.current?.dispose()
      performerRef.current = null
      setPlaying(false)
      return
    }
    let cancelled = false
    let created: Performer | null = null
    void (async () => {
      const p = await createScrubPerformer(buffer)
      if (cancelled) {
        p.dispose()
        return
      }
      performerRef.current = p
      created = p
    })()
    return () => {
      cancelled = true
      if (created) created.dispose()
      performerRef.current = null
      setPlaying(false)
    }
  }, [buffer])

  const positionForCol = useCallback(
    (col: number) => (col / COLS) * durationSec,
    [durationSec],
  )

  const pitchForY = useCallback((ny: number) => {
    // y=0 (top) → +12 st; y=1 (bottom) → -12 st. Detent ±0.5 st
    // around centre so "no shift" is easy to land on.
    const raw = (0.5 - ny) * 24
    return Math.abs(raw) < 0.5 ? 0 : raw
  }, [])

  const engageCell = useCallback(
    (col: number, ny: number) => {
      const perf = performerRef.current
      if (!perf || !buffer) return
      const pos = positionForCol(col)
      const pitch = pitchForY(ny)
      const zone = zoneDefForCol(col).zone as PerformerZone
      const variant = variantIndexForCol(col)
      perf.play({
        positionSec: pos,
        pitchSemitones: pitch,
        zone,
        variant,
        bpm: effectiveBpm,
        slicePositions,
        loopLengthBeats,
        grainSizeBeats: grainOverrideBeats,
        transposeSemitones: transpose,
        reverse,
      })
      setPlaying(true)
    },
    [
      buffer,
      positionForCol,
      pitchForY,
      effectiveBpm,
      slicePositions,
      loopLengthBeats,
      grainOverrideBeats,
      transpose,
      reverse,
    ],
  )

  const updateCell = useCallback(
    (_col: number, ny: number) => {
      const perf = performerRef.current
      if (!perf || !buffer) return
      // Intentionally DO NOT send positionSec here. `updateCell`
      // is called when the drag stays inside the same column, so
      // the sample position is unchanged. Passing positionSec would
      // hit the performer's hard-retrigger branch — and a pointer
      // drag at 60Hz + retriggering grains on every tick queued up
      // grain envelopes faster than they drained, which was the
      // "gets grainy and never recovers" symptom. Pitch / zone /
      // params update without a restart.
      const pitch = pitchForY(ny)
      perf.update({
        pitchSemitones: pitch,
        bpm: effectiveBpm,
        slicePositions,
        loopLengthBeats,
        grainSizeBeats: grainOverrideBeats,
        transposeSemitones: transpose,
        reverse,
      })
    },
    [
      buffer,
      pitchForY,
      effectiveBpm,
      slicePositions,
      loopLengthBeats,
      grainOverrideBeats,
      transpose,
      reverse,
    ],
  )

  // Push live-param edits to the performer while something is
  // playing, without retriggering. The performer's update() is
  // designed for this — slider drags propagate into grain emission
  // on the next tick without a clicky restart.
  useEffect(() => {
    if (!playing) return
    const perf = performerRef.current
    if (!perf) return
    perf.update({
      loopLengthBeats,
      grainSizeBeats: grainOverrideBeats,
      transposeSemitones: transpose,
      reverse,
    })
  }, [playing, loopLengthBeats, grainOverrideBeats, transpose, reverse])

  // Re-apply when BPM changes while latched (grain size follows
  // project tempo).
  useEffect(() => {
    if (playing && activeCol !== null) {
      updateCell(activeCol, activeY)
    }
  }, [playing, activeCol, activeY, updateCell])

  const cellFromPointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
      const col = Math.min(COLS - 1, Math.max(0, Math.floor(nx * COLS)))
      return { col, ny }
    },
    [],
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!buffer) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      dragPointerRef.current = e.pointerId
      const { col, ny } = cellFromPointer(e)
      setActiveCol(col)
      setActiveY(ny)
      engageCell(col, ny)
    },
    [buffer, cellFromPointer, engageCell],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragPointerRef.current !== e.pointerId) return
      const { col, ny } = cellFromPointer(e)
      // Only re-trigger when the column changes — staying inside
      // the same cell shouldn't re-start the grain emission (that
      // would click on every pointermove). Pitch (Y) updates
      // in-place without re-trigger.
      setActiveY(ny)
      if (col !== activeCol) {
        setActiveCol(col)
        engageCell(col, ny)
      } else {
        updateCell(col, ny)
      }
    },
    [cellFromPointer, activeCol, engageCell, updateCell],
  )

  const releasePointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragPointerRef.current !== e.pointerId) return
      dragPointerRef.current = null
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      if (!latched) {
        performerRef.current?.stop()
        setPlaying(false)
        setActiveCol(null)
      }
    },
    [latched],
  )

  const toggleLatch = useCallback(() => {
    setLatched(v => {
      const next = !v
      if (!next && playing && dragPointerRef.current === null) {
        performerRef.current?.stop()
        setPlaying(false)
        setActiveCol(null)
      }
      return next
    })
  }, [playing])

  const stopAll = useCallback(() => {
    performerRef.current?.stop()
    setPlaying(false)
    setActiveCol(null)
  }, [])

  const activeZone = activeCol !== null ? zoneDefForCol(activeCol).zone : null

  return (
    <section
      aria-label="Scrub performance pad"
      className="rounded-lg bg-surface-raised border border-brd-subtle"
    >
      <header className="px-3 pt-2 pb-1.5 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
          Scrub pad
        </span>
        <span className="text-[9px] text-content-tertiary/70 normal-case">
          4 zones · grains @ {effectiveBpm.toFixed(0)} BPM
        </span>
        {activeZone && playing && (
          <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-accent tabular-nums">
            · {activeZone}
            {activeCol !== null &&
              ` · ${VARIANT_LABELS[activeZone][variantIndexForCol(activeCol)]}`}
          </span>
        )}
        {/* Spacer — takes any leftover horizontal room so the
            action buttons pin to the right regardless of whether
            the active-zone chip is present. Having the spacer
            here (rather than `ml-auto` on the first button) means
            Latch stays rightmost and Stop slides in to its LEFT
            when playback starts — Latch's screen position never
            shifts under the user's finger. */}
        <div className="ml-auto" />
        {/* Bank-override picker — which bank's pad audio the scrub
            replaces while engaged. "None" = no override (default);
            scrub + pattern play in parallel like before. */}
        <label className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-content-tertiary">
          Overrides
          <select
            value={scrubTargetBankId ?? '__none__'}
            onChange={e => {
              const next = e.target.value
              setScrubTargetBankId(
                next === '__none__' ? null : (next as BankId),
              )
            }}
            title="While scrub is engaged, mute the selected bank's pad audio so the scrub pad takes over that part (e.g. a sliced drum break)."
            className="bg-surface-sunken text-content-primary rounded border border-brd-subtle px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
          >
            <option value="__none__">None</option>
            {BANK_PICKER_OPTIONS.map(b => (
              <option key={b} value={b}>
                Bank {b}
              </option>
            ))}
          </select>
        </label>
        {playing && (
          <button
            type="button"
            onClick={stopAll}
            title="Stop grain emission (tail keeps draining)"
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border border-destructive/60 text-destructive hover:bg-destructive/15"
          >
            <StopIcon size={9} />
            Stop
          </button>
        )}
        <button
          type="button"
          onClick={toggleLatch}
          disabled={!buffer}
          title={
            latched
              ? 'Latch on — drags leave the active cell engaged. Tap to release.'
              : 'Latch off — releasing the drag stops grains. Tap to pin.'
          }
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors disabled:opacity-50 ${
            latched
              ? 'bg-second text-[color:var(--second-ink,white)] border-second'
              : 'text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
          }`}
        >
          {latched ? <LockIcon size={10} /> : <UnlockIcon size={10} />}
          Latch
        </button>
      </header>

      {/* Zone label strip — four labels stretched across the
          corresponding four cells below. Uses grid-cols-16 so cell
          widths align exactly with the grid. */}
      <div
        className="grid px-1"
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
        aria-hidden
      >
        {ZONES.map((z, zi) => (
          <div
            key={z.zone}
            className={`col-span-4 text-[9px] font-mono uppercase tracking-[0.2em] text-content-tertiary text-center py-1 border-t ${
              activeZone === z.zone
                ? z.accent.replace('bg-', 'border-')
                : 'border-brd-subtle/40'
            }`}
            style={{ gridColumn: `${zi * 4 + 1} / span 4` }}
          >
            {z.label}
          </div>
        ))}
      </div>

      {/* The 16-cell grid. Single pointer surface so drag sweeps
          naturally cross cells. Cell boundaries render as 1px
          vertical dividers; zone boundaries (every 4 cells) render
          thicker. */}
      <div
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
        className={`relative grid h-32 mx-1 mb-1 rounded-md border border-brd-subtle bg-surface-sunken overflow-hidden touch-none select-none ${
          buffer ? 'cursor-crosshair' : 'cursor-not-allowed opacity-60'
        }`}
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
        aria-label="16-column performance grid"
        role="application"
      >
        {Array.from({ length: COLS }).map((_, col) => {
          const zoneDef = zoneDefForCol(col)
          const isActive = activeCol === col
          const isZoneBoundary = col % COLS_PER_ZONE === 0 && col !== 0
          return (
            <div
              key={col}
              className={`relative ${zoneDef.bg} ${
                isActive ? 'brightness-[1.7]' : ''
              } ${
                isZoneBoundary
                  ? 'border-l-2 border-brd-subtle'
                  : col !== 0
                    ? 'border-l border-brd-subtle/30'
                    : ''
              }`}
            >
              {/* Variant label — tiny caption centred at the bottom
                  of each cell so the user can see at a glance what
                  each of the 4 cells in a zone does. */}
              <div className="absolute inset-x-0 bottom-0.5 text-center text-[8px] font-mono tabular-nums text-content-tertiary/80 pointer-events-none">
                {VARIANT_LABELS[zoneDef.zone][variantIndexForCol(col)]}
              </div>
              {/* Active indicator — full-cell highlight bar + puck
                  dot at the pitch-Y position. */}
              {isActive && (
                <>
                  <div
                    aria-hidden
                    className={`absolute inset-0 ${zoneDef.accent} opacity-30 pointer-events-none`}
                  />
                  <div
                    aria-hidden
                    className="absolute left-1/2 w-3 h-3 -ml-1.5 -mt-1.5 rounded-full bg-accent border-2 border-[color:var(--accent-ink)] shadow-[0_0_14px_var(--accent-halo)] pointer-events-none"
                    style={{ top: `${activeY * 100}%` }}
                  />
                </>
              )}
            </div>
          )
        })}
        {/* Centre pitch line spanning the whole grid — visual
            reference for "no-shift" pitch. */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-1/2 h-px bg-content-tertiary/30 pointer-events-none"
          style={{ gridColumn: '1 / -1' }}
        />
      </div>

      {/* Live-control strip. Everything here updates the already-
          playing grain emission without retriggering, so the user
          can dial these while holding a cell with Latch on. Two
          rows so sliders don't wrap under narrow widths: primary
          row (Loop + Grain pills) and secondary row (Transpose +
          Reverse). */}
      <div className="px-3 py-2 border-t border-brd-subtle/40 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-12">
            Loop
          </span>
          <div
            role="radiogroup"
            aria-label="Loop length"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
          >
            {LOOP_LENGTHS.map(opt => {
              const active = loopLengthBeats === opt.beats
              return (
                <button
                  key={opt.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setLoopLengthBeats(opt.beats)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary ml-2 w-12">
            Grain
          </span>
          <div
            role="radiogroup"
            aria-label="Grain size override"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
            title="Auto = zone default; explicit values override the zone's grain clock so e.g. Gate can still use 1/32 grains underneath."
          >
            {GRAIN_OVERRIDES.map(opt => {
              const active = grainOverrideBeats === opt.beats
              return (
                <button
                  key={opt.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setGrainOverrideBeats(opt.beats)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-12">
            Transp.
          </span>
          <input
            type="range"
            min={-24}
            max={24}
            step={1}
            value={transpose}
            onChange={e => setTranspose(Number(e.target.value))}
            aria-label="Transpose semitones"
            title="Transpose offset layered on top of Y-axis pitch. Dial in a key offset, then use Y to modulate ±12 around it."
            className="w-36 accent-[color:var(--accent)]"
          />
          <span className="text-[10px] font-mono text-content-secondary tabular-nums w-10 text-right">
            {transpose >= 0 ? '+' : ''}
            {transpose} st
          </span>
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-content-secondary select-none ml-auto">
            <input
              type="checkbox"
              checked={reverse}
              onChange={e => setReverse(e.target.checked)}
              className="accent-[color:var(--accent)]"
            />
            Reverse
          </label>
        </div>
      </div>

      {/* Read-out strip: active cell info. Helps the user learn
          the mapping during early use. */}
      <div className="px-3 pb-2 flex items-center justify-between text-[9px] font-mono text-content-tertiary tabular-nums">
        <span>
          {activeCol !== null
            ? `col ${activeCol + 1}/16 · ${positionForCol(activeCol).toFixed(
                3,
              )}s · ${pitchForY(activeY) >= 0 ? '+' : ''}${pitchForY(
                activeY,
              ).toFixed(1)} st`
            : buffer
              ? 'tap any cell to engage'
              : 'load a sample to begin'}
        </span>
        <span className="text-content-tertiary/60 normal-case">
          {slices && slices.length > 1
            ? `${slices.length} slice bounce targets`
            : 'chop the sample to unlock Bounce'}
        </span>
      </div>
    </section>
  )
}
